/**
 * DcMaterialsStatsSection — подвкладка «Газета» (DC → Юнифанс): тотальная статистика
 * вкладки «Материалы». Открытия и читатели по дням, вовлечённость по рубрикам
 * (интервью / ЮСКАР / ролики гостей), действия (гороскоп, голосование, TikTok),
 * что трогают внутри, и самые активные читатели. Период — общий календарь вкладки.
 * Стиль — конституция DC: токены var(--*), цифры = var(--f-meta) tabular-nums.
 */
import { useState, type CSSProperties, type ReactElement } from "react";
import { useMaterialsStats, MAT_ACTION_LABELS, type MatRubric } from "../../hooks/useMaterialsStats";
import { AvatarImg, Bar, Kpi, NUM, box, capTitle, fmt, fmtDm, fmtWhen } from "./dcVideoStatsParts";
import { DcMontagesBlock } from "./DcMontagesBlock";
import { DcYuskarBlock } from "./DcYuskarBlock";

export function DcMaterialsStatsSection({ from, to, onImpersonate, onToast }: {
  onToast?: (m: string) => void; from?: string; to?: string; onImpersonate?: (tg: number) => void;
}): ReactElement {
  const { data, loading, error, reload } = useMaterialsStats(from, to);
  const [tab, setTab] = useState<"rubrics" | "elements" | "readers">("rubrics");

  const wrap: CSSProperties = { display: "grid", gap: 8, padding: 10, paddingBottom: "calc(12px + var(--safe-bottom))" };
  const seg = (on: boolean): CSSProperties => ({ flex: 1, padding: "4px 6px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", border: "1px solid " + (on ? "var(--purple)" : "var(--surface-3)"), background: on ? "color-mix(in srgb, var(--purple) 18%, transparent)" : "transparent", color: on ? "var(--text-1)" : "var(--text-2)" });

  if (loading && !data) return <div style={wrap}><div style={{ ...box, padding: 14, color: "var(--text-2)", fontSize: 12 }}>Загрузка статистики…</div></div>;
  if (error) return <div style={wrap}><div style={{ ...box, padding: 14, color: "var(--red)", fontSize: 12 }}>{error} <button type="button" onClick={reload} style={{ marginLeft: 8, color: "var(--role)", background: "none", border: "none", cursor: "pointer" }}>Повторить</button></div></div>;
  if (!data) return <div style={wrap} />;

  const s = data.summary;
  const dayMax = Math.max(1, ...data.daily.map((d) => d.opens));
  const elMax = Math.max(1, ...data.elements.map((e) => e.n));

  // Строка рубрики: показы / зрители / минуты / досмотр.
  const rubricRow = (title: string, r: MatRubric, i: number): ReactElement => (
    <div key={title} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", minHeight: 34, padding: "4px 8px", borderTop: i ? "1px solid var(--surface-3)" : "none" }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ fontSize: 12.5, color: "var(--text-1)", display: "block" }}>{title}</span>
        <span style={{ ...NUM, fontSize: 9.5, color: "var(--text-3)" }}>{fmt(r.watchMin)} мин · досмотр {fmt(r.completionRate)}%</span>
      </span>
      <span style={{ ...NUM, fontSize: 11.5, color: "var(--text-2)", textAlign: "right" }}>
        <b style={{ color: "var(--text-1)" }}>{fmt(r.plays)}</b> пок · {fmt(r.viewers)} зр
      </span>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={capTitle}>Газета · статистика</span>
        <button type="button" onClick={reload} title="Обновить" style={{ display: "inline-grid", placeItems: "center", width: 26, height: 26, borderRadius: 6, border: "1px solid var(--surface-3)", background: "transparent", color: "var(--text-2)", cursor: "pointer" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square"><path d="M20 11a8 8 0 1 0-2.3 5.7M20 4v5h-5" /></svg>
        </button>
      </div>

      <DcYuskarBlock onImpersonate={onImpersonate} from={from} to={to} />

      <DcMontagesBlock onToast={onToast} />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
        <Kpi label="Открытий" value={fmt(s.opens)} sub={`${fmt(s.opensPerReader)} на чит.`} />
        <Kpi label="Читателей" value={fmt(s.readers)} sub={`всего ${fmt(s.users)}`} />
        <Kpi label="Действий" value={fmt(s.taps)} sub={`${fmt(s.tapsPerReader)} на чит.`} />
      </div>

      <div style={{ ...box, padding: 8, display: "grid", gap: 4 }}>
        <span style={capTitle}>По дням</span>
        {data.daily.length === 0
          ? <div style={{ padding: "6px 2px", color: "var(--text-3)", fontSize: 12 }}>За период открытий не было</div>
          : data.daily.map((d) => <Bar key={d.d} label={fmtDm(d.d)} count={d.opens} max={dayMax} hint={`· ${fmt(d.readers)} чит.`} />)}
      </div>

      <div style={{ display: "flex", gap: 4 }}>
        {(["rubrics", "elements", "readers"] as const).map((t) => (
          <button key={t} type="button" onClick={() => setTab(t)} style={seg(tab === t)}>
            {t === "rubrics" ? "Рубрики" : t === "elements" ? "Что трогают" : "Читатели"}
          </button>
        ))}
      </div>

      {tab === "rubrics" ? (
        <>
          <div style={{ ...box, overflow: "hidden" }}>
            {rubricRow("Интервью", data.rubrics.interview, 0)}
            {rubricRow("ЮСКАР · видео номинантов", data.rubrics.yuskar, 1)}
            {rubricRow("Снято гостями", data.rubrics.guest, 2)}
          </div>
          <div style={{ ...box, padding: 8, display: "grid", gap: 4 }}>
            <span style={capTitle}>Действия в газете</span>
            {data.actions.length === 0
              ? <div style={{ padding: "6px 2px", color: "var(--text-3)", fontSize: 12 }}>Действий за период нет</div>
              : data.actions.map((a) => (
                <div key={a.kind} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center", minHeight: 26 }}>
                  <span style={{ fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{MAT_ACTION_LABELS[a.kind] ?? a.kind}</span>
                  <span style={{ ...NUM, fontSize: 12, color: "var(--text-1)", fontWeight: 700 }}>{fmt(a.n)} <span style={{ color: "var(--text-3)", fontWeight: 400 }}>· {fmt(a.users)} чел.</span></span>
                </div>
              ))}
          </div>
        </>
      ) : tab === "elements" ? (
        <div style={{ ...box, padding: 8, display: "grid", gap: 4 }}>
          {data.elements.length === 0
            ? <div style={{ padding: "6px 2px", color: "var(--text-3)", fontSize: 12 }}>Нажатий за период нет</div>
            : data.elements.map((e) => <Bar key={e.label} label={e.label} count={e.n} max={elMax} hint={`· ${fmt(e.users)} чел.`} />)}
        </div>
      ) : (
        <div style={{ ...box, overflow: "hidden" }}>
          {data.topReaders.length === 0 ? <div style={{ padding: 12, color: "var(--text-3)", fontSize: 12 }}>Читателей за период нет</div>
            : data.topReaders.map((r, i) => (
              <button key={r.tg} type="button" onClick={() => onImpersonate?.(r.tg)}
                style={{ display: "grid", gridTemplateColumns: "26px 1fr auto auto", gap: 8, alignItems: "center", width: "100%", minHeight: 30, padding: "3px 6px", border: "none", borderTop: i ? "1px solid var(--surface-3)" : "none", background: "transparent", color: "var(--text-1)", cursor: onImpersonate ? "pointer" : "default", textAlign: "left" }}>
                <AvatarImg url={r.avatarUrl} name={r.name || r.username} size={26} />
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12.5 }}>{r.name || (r.username ? "@" + r.username : "id " + r.tg)}</span>
                <span style={{ ...NUM, fontSize: 12, color: "var(--text-2)" }}>{fmt(r.opens)} откр · {fmt(r.events)} соб</span>
                <span style={{ ...NUM, fontSize: 10, color: "var(--text-3)" }}>{fmtWhen(r.lastSeen)}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
